  // TODO - Type 'any' needs to be fixed.
/* eslint-disable no-mixed-spaces-and-tabs */
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HighlightOutlinedIcon from "@mui/icons-material/HighlightOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import {Chip, Skeleton, Tooltip} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import format from "date-fns/format";
import isToday from "date-fns/isToday";
import isTomorrow from "date-fns/isTomorrow";
import isYesterday from "date-fns/isYesterday";
import React, {useCallback} from "react";
import { useTranslation } from "react-i18next";

import {SkillRepresentationCell} from "../../../../../export/cross-skilling";
import {formatDate, getConfirmedWorkersCount, getRepLevel, getRepType,} from "../../../../../export/helpers";
import {JOB, LOCATION, PRIMARYJOB, SENIORITY, SKILLS, WCFIFO, WORKCENTER, } from "../../../../assets/constants";
import { colors } from "../../../../config/colors";
import ExpandableText from "../../../../design/expandable-text";
import JobColor from "../../../../design/ui/job-color";
import { getAcceptanceCutoffTime } from "../../../../utils/acceptance-cutoff";
import {formatDurationAbbreviated, getHourMinuteDifference} from "../../../../utils/helpers";
import { getShiftPurposeDetailField } from "../../../../utils/shift-purpose-detail-field";
import useSchedulerData from "../../controllers/use-scheduler-data";

interface ShiftMetaDataProps {
  shiftMetadata: any;
  jobRoleMetadata: any;
  workCenterMetadata: any;
  allSkillLevels: any;
  confirmedWorkersCount: number | undefined;
  showSkeleton?: boolean;
  totalFullSpotsConfirmed?: number;
  totalHalfSpotsConfirmed?: number;
  totalFlexiSpotsConfirmed?: number;
  totalFlexiSpotsTotal?: number;
}

const ShiftMetaData: React.FC<ShiftMetaDataProps> = ({
  shiftMetadata,
  jobRoleMetadata,
  workCenterMetadata,
  allSkillLevels,
  confirmedWorkersCount,
  showSkeleton = false,
  totalFullSpotsConfirmed,
  totalHalfSpotsConfirmed,
  totalFlexiSpotsConfirmed,
  totalFlexiSpotsTotal,
}) => {
  const { t } = useTranslation();
  const { shiftPurposes, isShiftPurposeEnabled, workerTypesForCreateShift: workerTypes } = useSchedulerData();
  const shiftByType = shiftMetadata?.jobId ? JOB : SKILLS;
  // What the Shift Purpose asked the supervisor for, so the answer can be
  // labelled with the question it was given for.
  const shiftPurposeDetailField = getShiftPurposeDetailField(
    shiftPurposes,
    shiftMetadata?.shiftPurposeId
  );
  // for flex scheduler we use skills value and for worker requests we use shiftSkills below
  // According to new changes in signup tracking API for worker request shift details dialog
  const skills = shiftMetadata?.skills || shiftMetadata?.shiftSkills;

  const allSkillsObj = skills?.map((skill: any) => {
    const matchingObject = allSkillLevels.find(
      (level: any) => level.level === skill.level
    );
    if (matchingObject) {
      const { repCode, foreColorCode, backColorCode, value } = matchingObject;
      return {
        ...skill,
        repCode,
        foreColorCode,
        backColorCode,
        value,
      };
    } else {
      return null;
    }
  });

  const closedForInterest = shiftMetadata?.windowClosingTime
    ? new Date().getTime() >=
      new Date(shiftMetadata.windowClosingTime).getTime()
    : false;

  const remainingHours = shiftMetadata?.windowClosingTime
    ? getHourMinuteDifference(
        new Date(shiftMetadata.windowClosingTime),
        new Date()
      ).differenceInText + " " + t("scheduler_left")
    : -1;

  // Per-Shift acceptance cutoff. Only rendered when the Shift carries its own
  // override; the Location default is enforced server-side and not shown here.
  const acceptanceCutoffTime =
    shiftMetadata?.isShiftAcceptanceCutoffEnabled && shiftMetadata?.startDateTime
      ? getAcceptanceCutoffTime(
          shiftMetadata.startDateTime,
          shiftMetadata.minHoursBeforeShiftStartToAccept ?? 0
        )
      : null;

  const acceptanceClosed = acceptanceCutoffTime
    ? new Date().getTime() >= acceptanceCutoffTime.getTime()
    : false;

  const acceptanceRemaining = acceptanceCutoffTime
    ? getHourMinuteDifference(acceptanceCutoffTime, new Date())
        .differenceInText
    : "";

  // "Today 2:00 PM", "Tomorrow 6:30 AM", "Yesterday 8:00 PM" or the full date.
  const getRelativeTimeLabel = (date: Date): string => {
    if (isToday(date)) {
      return `${t("scheduler_today")} ${format(date, "h:mm a")}`;
    }
    if (isTomorrow(date)) {
      return `${t("scheduler_tomorrow")} ${format(date, "h:mm a")}`;
    }
    if (isYesterday(date)) {
      return `${t("scheduler_yesterday")} ${format(date, "h:mm a")}`;
    }
    return formatDate({
      dateStr: date.toISOString(),
      formatType: "DATE_WITH_TIME",
    });
  };

  const acceptanceText = acceptanceCutoffTime
    ? acceptanceClosed
      ? t("scheduler_acceptance-cutoff-closed", {
          time: getRelativeTimeLabel(acceptanceCutoffTime),
        })
      : t("scheduler_acceptance-cutoff-closes", {
          time: getRelativeTimeLabel(acceptanceCutoffTime),
          remaining: acceptanceRemaining,
        })
    : "";

  const hasAllocationWindow =
    shiftMetadata?.shiftAllocationType === SENIORITY ||
    shiftMetadata?.shiftAllocationType === WORKCENTER ||
    shiftMetadata?.shiftAllocationType === LOCATION ||
    shiftMetadata?.shiftAllocationType === PRIMARYJOB ||
    shiftMetadata?.shiftAllocationType === WCFIFO;

  // Once the interest window has shut, the acceptance cutoff becomes the next
  // deadline, so it is read out on the same line (and in the same muted style)
  // rather than as a separate red warning that competes with "window closed".
  const windowText = (() => {
    if (!shiftMetadata?.windowClosingTime) {
      return "";
    }
    const windowLabel = getRelativeTimeLabel(
      new Date(shiftMetadata.windowClosingTime)
    );
    if (!closedForInterest) {
      const openText = `${windowLabel} (${remainingHours})`;
      return acceptanceClosed ? `${openText}. ${acceptanceText}` : openText;
    }
    if (!acceptanceText) {
      return `${windowLabel} (${t("scheduler_window-closed")})`;
    }
    return `${t("scheduler_window-closed-at", {
      time: windowLabel,
    })}. ${acceptanceText}`;
  })();

  const getWorkCenterName = useCallback(() => {
    let returnValue = "-";
    if (workCenterMetadata) {
      returnValue = workCenterMetadata.name;
    }
    return returnValue;
  }, [workCenterMetadata]);

  const getLocationName = useCallback(() => {
    const returnValue = "-";
    if (workCenterMetadata) {
      if (
        Object.prototype.hasOwnProperty.call(workCenterMetadata, "location")
      ) {
        if (
          Object.prototype.hasOwnProperty.call(
            workCenterMetadata.location,
            "name"
          )
        ) {
          return workCenterMetadata.location.name;
        }
      }
    }
    return returnValue;
  }, [workCenterMetadata]);
  if (showSkeleton)
    return (
      <Box
        sx={{
          padding: "24px 24px 0px 24px",
          width: "100%",
          maxWidth: "1155px",
        }}
      >
        <Box mb={1.5}>
          <Typography marginLeft="4px" variant="h6">
            <Skeleton width={120} />
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
            }}
          >
            {Array.from({ length: 3 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "4px",
                  alignItems: "center",
                }}
              >
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton width="70%" />
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
            }}
          >
            {Array.from({ length: 3 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton width="70%" />
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Skeleton variant="rectangular" width={16} height={16} />
            <Skeleton width="40%" />
          </Box>
        </Box>
      </Box>
    );
  if (!shiftMetadata) return null;
  return (
    <Box
      sx={{
        padding: "24px 24px 0px 24px",
        width: "100%",
        maxWidth: "1155px",
      }}
    >
      <Box mb={1.5} display="flex" alignItems="center" gap={2}>
        <Typography marginLeft="4px" variant="h6"
                    sx={{
                      fontFamily: "Roboto",
                    }}
        >
          {t("scheduler_shift-details")}
        </Typography>
        {shiftMetadata?.isUrgent && (
          <Box
            sx={{
              padding: "2px 8px",
              display: "inline-flex",
              flexDirection: "row",
              alignItems: "center",
              border: 1,
              borderWidth: "1px",
              borderColor: "#D32F2F",
              borderRadius: "4px",
              backgroundColor: "#FFEBEE",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto",
                color: "#D32F2F",
                fontSize: "12px",
                fontWeight: "500",
                lineHeight: "20px",
                letterSpacing: "0.4px",
              }}
            >
              {t("scheduler_urgent")}
            </Typography>
            <ErrorOutlineIcon
              sx={{
                color: "#D32F2F",
                fontSize: "16px",
                marginLeft: "4px",
              }}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isShiftPurposeEnabled &&
              shiftMetadata.shiftPurposeId &&
              shiftPurposes ? "1fr 1fr 1fr" : "1fr 1fr",
          }}
        >
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <LocationOnOutlinedIcon
                sx={{
                  color: "#0000008f",
                  fontSize: "16px",
                }}
              />
              <Typography sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
              }}>{`${shiftMetadata?.shiftLocationName} - ${shiftMetadata?.shiftWorkcenterName}`}</Typography>
            </Box>
          </>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <PersonOutlinedIcon sx={{
              fontSize: "16px",
              color: "#0000008f"
            }} />
            {shiftMetadata &&
              <Typography sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
              }}>
                {(() => {
                  const needed = parseInt(shiftMetadata?.numberOfWorkersNeeded) || 0;
                  const isFlexi = shiftMetadata?.isFlexiSignupEnabled;
                  const isPartial = shiftMetadata?.isPartialSignupEnabled;

                  if (isFlexi || isPartial) {
                    const shiftStart = new Date(shiftMetadata?.startDate || shiftMetadata?.startDateTime);
                    const shiftEnd = new Date(shiftMetadata?.endDate || shiftMetadata?.endDateTime);
                    const shiftDurationHours = (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60);
                    const totalHoursRequiredRaw = needed * shiftDurationHours;
                    const coveredHoursRaw = (shiftMetadata?.slotsFilled || 0) * shiftDurationHours;

                    if (coveredHoursRaw === 0) {
                      return `${t("scheduler_open")} • ${formatDurationAbbreviated(totalHoursRequiredRaw)}`;
                    } else {
                      const percentage = needed > 0 ? Math.round(((shiftMetadata?.slotsFilled || 0) / needed) * 100) : 0;
                      return `${percentage}% • ${formatDurationAbbreviated(coveredHoursRaw)}/${formatDurationAbbreviated(totalHoursRequiredRaw)}`;
                    }
                  } else {
                    const signedUp = confirmedWorkersCount !== undefined ? confirmedWorkersCount : getConfirmedWorkersCount(shiftMetadata);
                    if (signedUp === 0) {
                      return `${t("scheduler_open")} • ${needed} ${needed > 1 ? t("scheduler_workers") : t("scheduler_worker")}`;
                    } else {
                      return `${signedUp}/${needed} ${t("scheduler_workers")}`;
                    }
                  }
                })()}
              </Typography>
            }
          </Box>
          {isShiftPurposeEnabled &&
          shiftMetadata.shiftPurposeId &&
          shiftPurposes ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <HighlightOutlinedIcon
                sx={{ color: "#0000008f",
                  transform: "rotate(270deg)",
                  fontSize: "16px",
              }}
              />
              <Typography>
                {shiftPurposes[shiftMetadata.shiftPurposeId].name ?? "-"}
              </Typography>
            </Box>
          ) : null}
        </Box>
        {isShiftPurposeEnabled && shiftMetadata?.shiftPurposeDetail ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
              alignItems: "flex-start",
            }}
          >
            <QuestionAnswerOutlinedIcon
              sx={{ color: "#0000008f", fontSize: "16px", marginTop: "4px" }}
            />
            <ExpandableText
              text={
                shiftPurposeDetailField
                  ? `${shiftPurposeDetailField.label}: ${shiftMetadata.shiftPurposeDetail}`
                  : shiftMetadata.shiftPurposeDetail
              }
            />
          </Box>
        ) : null}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: shiftMetadata?.allowedWorkerTypeIds?.length > 0 ? "1fr 1fr 1fr" : "1fr 1fr",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: "8px",
            }}
          >
            <TodayOutlinedIcon
              sx={{
                color: "#0000008f",
                fontSize: "16px",
              }}
            />
            <Typography sx={{
              fontFamily: "Roboto",
              fontSize: "14px",
            }}>
              {shiftMetadata?.startDateTime &&
                shiftMetadata?.endDateTime &&
                `${formatDate({
                  dateStr: shiftMetadata?.startDateTime,
                  formatType: "SHORT_DATE",
                  endDateStr: shiftMetadata?.endDateTime,
                })} ${formatDate({
                  dateStr: shiftMetadata?.startDateTime,
                  formatType: "TIME_RANGE",
                  endDateStr: shiftMetadata?.endDateTime,
                })}`}
              {shiftMetadata?.isFlexiSignupEnabled && shiftMetadata?.minFlexiSignupHours > 0 && (
                <Box component="span" sx={{ fontWeight: "500", ml: 1 }}>
                  {`(${t("scheduler_min")} ${shiftMetadata.minFlexiSignupHours} ${shiftMetadata.minFlexiSignupHours > 1 ? t("scheduler_hrs") : t("scheduler_hr")})`}
                </Box>
              )}
            </Typography>
          </Box>
          {hasAllocationWindow && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "8px",
              }}
            >
              {closedForInterest ? (
                <TimerOffOutlinedIcon
                  sx={{
                    color: "#0000008f",
                    fontSize: "16px",
                  }}
                />
              ) : (
                <TimerOutlinedIcon
                  sx={{
                    color: "#0000008f",
                    fontSize: "16px",
                  }}
                />
              )}
              <Typography sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
                color: closedForInterest ? "#0000008f" : "#000000",
              }}>
                {windowText}
              </Typography>
              {closedForInterest && (
                <Tooltip title={t("scheduler_shift-assign-info")}>
                  <InfoOutlinedIcon
                    sx={{
                      margin: "4px",
                      color: "#0000008f",
                      fontSize: "16px",
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          )}
          {acceptanceCutoffTime && !hasAllocationWindow && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "8px",
              }}
            >
              {acceptanceClosed ? (
                <TimerOffOutlinedIcon
                  sx={{
                    color: "#0000008f",
                    fontSize: "16px",
                  }}
                />
              ) : (
                <TimerOutlinedIcon
                  sx={{
                    color: "#0000008f",
                    fontSize: "16px",
                  }}
                />
              )}
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  color: acceptanceClosed ? "#0000008f" : "#000000",
                }}
              >
                {acceptanceText}
              </Typography>
            </Box>
          )}
          {shiftMetadata?.allowedWorkerTypeIds?.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <BadgeOutlinedIcon
                sx={{ color: "#0000008f", fontSize: "16px" }}
              />
              {shiftMetadata.allowedWorkerTypeIds.map((id: number) => (
                <Chip
                  key={id}
                  label={workerTypes.find((wt) => wt.id === id)?.name ?? id}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "12px" }}
                />
              ))}
              <Tooltip title={t("scheduler_worker-types-eligible-info")}>
                <InfoOutlinedIcon
                  sx={{
                    margin: "4px",
                    color: "#0000008f",
                    fontSize: "16px",
                  }}
                />
              </Tooltip>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
          }}
        >
          {shiftByType === JOB ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1,
              }}
            >
              <WorkHistoryOutlinedIcon
                sx={{
                  color: "#0000008f",
                  fontSize: "16px",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                }}
              >
                <JobColor
                  color={`#${shiftMetadata?.shiftJobRole?.hexColor ?? "#fff"}`}
                />
                <Typography sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                }}>
                  {shiftMetadata?.shiftJobRole?.name ?? ""}
                </Typography>
                {shiftMetadata?.shiftJobRole?.isActive ? null : (
                  <Chip
                    label={t("scheduler_deactivated")}
                    variant="outlined"
                    color="warning"
                    size="small"
                    sx={{
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  />
                )}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.5,
              }}
            >
              <BuildOutlinedIcon
                sx={{
                  color: "#0000008f",
                  fontSize: "16px",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                {allSkillsObj?.map((skillLevel: any) => (
                  <Box
                    key={skillLevel.skillName}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <SkillRepresentationCell
                      level={skillLevel.level}
                      backgroundColor={skillLevel.backColorCode}
                      foregroundColor={skillLevel.foreColorCode}
                      value={skillLevel.value}
                      representationType={getRepType(skillLevel.repCode)}
                      representationLevel={getRepLevel(
                        skillLevel.repCode,
                        skillLevel.level
                      )}
                      border={1}
                      height={18}
                      width={18}
                      backgroundHeight={
                        skillLevel.backColorCode ? "28px" : "20px"
                      }
                      backgroundWidth={
                        skillLevel.backColorCode ? "28px" : "20px"
                      }
                    />
                    <Typography sx={{
                      fontFamily: "Roboto",
                      fontSize: "14px",
                    }}>{skillLevel.skillName}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
        {shiftMetadata.shiftNote ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              maxWidth: 1100,
              gap: "8px",
            }}
          >
            <StickyNote2OutlinedIcon
              sx={{ color: "#0000008f", fontSize: "16px", marginTop: "4px" }}
            />
            <ExpandableText text={shiftMetadata.shiftNote} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default ShiftMetaData;
